import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useLocalStorage } from 'usehooks-ts'
import { useTranslations, useLocale, localizedPath } from '@/utils/locale'
import { getAltPath } from '@/utils/strings'
import Routes from '@/constants/routes'

/**
 * Copy the markdown content of the current help article to clipboard
 */
const copyPageAsMarkdown = async (url: string): Promise<boolean> => {
  try {
    // Build markdown URL using getAltPath
    const markdownPath = getAltPath(url, '.md')
    const response = await fetch(markdownPath)

    if (!response.ok) {
      throw new Error('Failed to fetch markdown')
    }

    const markdownContent = await response.text()
    await navigator.clipboard.writeText(markdownContent)

    return true
  } catch (error) {
    console.error('Failed to copy markdown:', error)

    // Fallback: copy current page URL
    try {
      await navigator.clipboard.writeText(`${Routes.hostname}${url}`)
      return true
    } catch (fallbackError) {
      console.error('Failed to copy fallback URL:', fallbackError)
      return false
    }
  }
}

export function useMarkdownCopy() {
  const [copied, setCopied] = useState(false)

  const handleCopyMarkdown = async (url: string): Promise<boolean> => {
    const success = await copyPageAsMarkdown(url)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
    return success
  }

  return { copied, handleCopyMarkdown }
}

export function useAIChat() {
  const { t } = useTranslations()
  const { currentLocale } = useLocale()
  const [, setSmartSearchQuery] = useLocalStorage<string | undefined>('smartSearchQuery', undefined)

  const openAIChat = (url: string) => {
    const markdownUrl = getAltPath(url, '.md')
    const fullMarkdownUrl = `${Routes.hostname}${markdownUrl}`
    const prompt = t('Read from ${url} so I can ask questions about it.').replace('${url}', fullMarkdownUrl)

    setSmartSearchQuery(prompt)
    router.visit(localizedPath('/smart-search/', currentLocale))
  }

  return { openAIChat }
}